import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import toast from 'react-hot-toast';

import { adminLearningPathsApi } from '@/services/adminLearningPaths';
import { PATHS } from '@/routes/paths';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Table, TableBody, TableCell, TableHeaderWrapper, TableNoData, TableRow } from '@/components/ui/table';
import { Sheet, SheetContent, SheetHeader, SheetBody, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { Filter, FilterContent, FilterRow } from '@/components/custom/filter';
import { Loader } from '@/components/custom/loader';
import { DashboardContent } from '@/layouts/dashboard';

const STATUS_CONFIG = {
  active: { label: 'Em andamento', variant: 'warning' },
  paused: { label: 'Pausada', variant: 'neutral-lightest' },
  completed: { label: 'Concluída', variant: 'success' }
};

const BLOCK_STATE = {
  done: { label: 'Concluído', variant: 'success' },
  skipped: { label: 'Pulado', variant: 'warning' },
  pending: { label: 'Pendente', variant: 'neutral-lightest' }
};

const BLOCK_KIND = {
  content: 'Conteúdo',
  review: 'Revisão'
};

const TABLE_HEAD = [{ label: 'Aluno' }, { label: 'Status', width: 130 }, { label: '% concluído', width: 110 }, { label: '% dominado', width: 110 }, { label: 'Início', width: 160 }, { label: 'Último acesso', width: 160 }];

function formatDate(value) {
  if (!value) return '—';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString('pt-BR');
}

export default function TrilhaStudents() {
  const { learningPathId } = useParams();
  const [rows, setRows] = useState([]);
  const [trilhaName, setTrilhaName] = useState('');
  const [pagination, setPagination] = useState({ currentPage: 1, totalPages: 1, totalCount: 0 });
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchTerm.trim()), 300);
    return () => clearTimeout(t);
  }, [searchTerm]);

  const load = (page = 1) => {
    setLoading(true);
    return adminLearningPathsApi
      .students(learningPathId, { q: debouncedSearch, page, perPage: 25 })
      .then(json => {
        const payload = json?.data || [];
        setRows(payload.map(d => ({ id: d.id, ...d.attributes })));
        const meta = json?.meta || {};
        setTrilhaName(meta.learning_path?.name || '');
        setPagination({
          currentPage: meta.current_page || 1,
          totalPages: meta.total_pages || 1,
          totalCount: meta.total_count || payload.length
        });
      })
      .catch(() => {
        toast.error('Erro ao carregar alunos da trilha');
        setRows([]);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [learningPathId, debouncedSearch]);

  // Drill-down: carrega os blocos do aluno selecionado.
  useEffect(() => {
    if (!selected) {
      setDetail(null);
      return;
    }
    setDetailLoading(true);
    adminLearningPathsApi
      .studentDetail(selected.id)
      .then(json => setDetail(json?.data?.attributes || null))
      .catch(() => toast.error('Erro ao carregar progresso do aluno'))
      .finally(() => setDetailLoading(false));
  }, [selected]);

  const breadcrumbs = {
    title: trilhaName || 'Alunos da trilha',
    nav: [
      { route: PATHS.TRILHAS_ADMIN.LIST, name: 'Gerenciar trilhas' },
      { name: 'Alunos', isActive: true }
    ]
  };

  return (
    <DashboardContent
      variant="plain"
      breadcrumbs={breadcrumbs}
    >
      <Filter variant="plain">
        <FilterContent>
          <FilterRow className="min-w-[280px]">
            <Label htmlFor="student-search">Buscar aluno</Label>
            <Input
              id="student-search"
              type="search"
              placeholder="Nome ou e-mail"
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
            />
          </FilterRow>
        </FilterContent>
      </Filter>

      {loading ? (
        <Loader />
      ) : (
        <>
          <p className="mb-2 text-sm text-neutral">{pagination.totalCount} aluno(s) iniciaram esta trilha.</p>
          <Table className="min-w-[900px]">
            <TableHeaderWrapper tableHead={TABLE_HEAD} />
            <TableBody>
              {rows.map(r => {
                const cfg = STATUS_CONFIG[r.status] || STATUS_CONFIG.active;
                return (
                  <TableRow
                    key={r.id}
                    className="cursor-pointer"
                    onClick={() => setSelected(r)}
                  >
                    <TableCell>
                      <p className="mb-0 font-medium text-primary-dark">{r.user_name}</p>
                      <p className="mb-0 text-xs text-neutral">{r.user_email}</p>
                    </TableCell>
                    <TableCell>
                      <Badge variant={cfg.variant}>{cfg.label}</Badge>
                    </TableCell>
                    <TableCell className="tabular-nums">{Math.round(r.percent_walked ?? 0)}%</TableCell>
                    <TableCell className="tabular-nums">{Math.round(r.percent_mastered ?? 0)}%</TableCell>
                    <TableCell className="tabular-nums">{formatDate(r.generated_at)}</TableCell>
                    <TableCell className="tabular-nums">{formatDate(r.last_accessed_at)}</TableCell>
                  </TableRow>
                );
              })}
              <TableNoData
                notFound={rows.length === 0}
                colSpan={TABLE_HEAD.length}
              />
            </TableBody>
          </Table>

          {pagination.totalPages > 1 && (
            <div className="mt-4 flex justify-center gap-2">
              {Array.from({ length: pagination.totalPages }, (_, i) => i + 1).map(p => (
                <Button
                  key={p}
                  size="xs"
                  variant={p === pagination.currentPage ? 'default' : 'outline'}
                  onClick={() => load(p)}
                >
                  {p}
                </Button>
              ))}
            </div>
          )}
        </>
      )}

      <Sheet
        open={Boolean(selected)}
        onOpenChange={isOpen => {
          if (!isOpen) setSelected(null);
        }}
      >
        <SheetContent
          size="lg"
          className="overflow-y-auto"
        >
          <SheetHeader>
            <SheetTitle>{selected?.user_name}</SheetTitle>
            <SheetDescription>
              Progresso na trilha {detail?.learning_path_name || trilhaName || ''} — {Math.round(selected?.percent_walked ?? 0)}% concluído, {Math.round(selected?.percent_mastered ?? 0)}% dominado.
            </SheetDescription>
          </SheetHeader>
          <SheetBody>
            {detailLoading ? (
              <p className="py-4 text-center text-sm text-neutral">Carregando blocos...</p>
            ) : (
              <ul className="space-y-2">
                {(detail?.blocks || []).map(b => {
                  const state = BLOCK_STATE[b.state] || BLOCK_STATE.pending;
                  return (
                    <li
                      key={b.position}
                      className="flex items-center gap-3 rounded-lg border border-neutral-lightest bg-white p-3"
                    >
                      <span className="w-8 shrink-0 text-center font-mono text-xs text-neutral">{b.position}</span>
                      <div className="min-w-0 flex-1">
                        <p className="mb-0 truncate text-sm font-medium text-primary-dark">{b.video_title || b.taxonomy_name || BLOCK_KIND[b.kind]}</p>
                        <p className="mb-0 text-xs text-neutral">
                          {BLOCK_KIND[b.kind] || b.kind}
                          {b.completed_at ? ` · concluído em ${formatDate(b.completed_at)}` : ''}
                        </p>
                      </div>
                      <Badge variant={state.variant}>{state.label}</Badge>
                    </li>
                  );
                })}
                {(detail?.blocks || []).length === 0 && <p className="py-4 text-center text-sm text-neutral">Sem blocos para exibir.</p>}
              </ul>
            )}
          </SheetBody>
        </SheetContent>
      </Sheet>
    </DashboardContent>
  );
}
